import React, { useState, useEffect, useCallback } from 'react';
import {
    X, CheckSquare, Award, AlertTriangle, Users,
    Clock, TrendingUp, BarChart3, Shield,
    Download, RefreshCw, Puzzle, AlertCircle, PenTool, Upload,
    Maximize2, Minimize2, Bell, Send, Zap
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    ProgressRing,
    DonutChart,
    LineChart,
    DataTable,
    ExpandableVisualization
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';
import ReminderHistory from './ReminderHistory';

const OverviewAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [trendsData, setTrendsData] = useState(null);
    const [placeholderData, setPlaceholderData] = useState(null);
    const [showPlaceholders, setShowPlaceholders] = useState(false);
    const [reminderSummary, setReminderSummary] = useState(null);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 400, maxWidth: 900, defaultWidth: 700,
        storageKey: 'overviewAnalyticsDrawerWidth',
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [overviewRes, trendsRes, placeholderRes, reminderRes] = await Promise.all([
                api.get('/analytics/overview', { headers }),
                api.get('/analytics/trends', { headers }),
                api.get('/analytics/placeholder-usage', { headers }).catch(() => ({ data: { analytics: null } })),
                api.get('/reminders/summary', { headers }).catch(() => ({ data: { summary: null } }))
            ]);

            setOverview(overviewRes.data.overview);
            setTrendsData(trendsRes.data.trends);
            setPlaceholderData(placeholderRes.data.analytics);
            setReminderSummary(reminderRes.data.summary);
        } catch (error) {
            console.error('Error fetching overview analytics:', error);
            toast.error('Failed to load overview analytics');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'comprehensive', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'comprehensive-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Comprehensive analytics exported');
        } catch { toast.error('Export failed'); }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
            document.body.style.overflow = 'hidden';
        } else {
            setOverview(null);
            setTrendsData(null);
            setPlaceholderData(null);
            setReminderSummary(null);
            document.body.style.overflow = 'unset';
            setLoading(true);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Format time helper
    const formatTime = (minutes) => {
        if (!minutes) return '-';
        if (minutes < 60) return `${Math.round(minutes)}m`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end transform transition-transform duration-300">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{
                    width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                    maxWidth: '100vw',
                    transition: isDragging ? 'none' : 'width 0.3s ease',
                }}
            >
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}

                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <BarChart3 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Analytics Overview</h2>
                            <p className="text-sm text-gray-500">Comprehensive performance insights</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refresh Data"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content — min-w-0 ensures content reflows instead of squishing */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide min-w-0">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                            <p className="text-gray-500 font-medium">Loading analytics...</p>
                        </div>
                    ) : overview ? (
                        <div className="space-y-6">
                            {/* Key Metrics */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <StatCard
                                    title="Total Employees"
                                    value={overview.employees.total}
                                    subtitle={`${overview.employees.onboarding} onboarding`}
                                    icon={Users}
                                    color="blue"
                                />
                                <StatCard
                                    title="Task Completion Rate"
                                    value={`${overview.tasks.completionRate}%`}
                                    subtitle={`${overview.tasks.completed} of ${overview.tasks.total} tasks`}
                                    icon={CheckSquare}
                                    color="blue"
                                />
                                <StatCard
                                    title="Avg Quiz Score"
                                    value={`${overview.quizzes.avgScore}%`}
                                    subtitle={`${overview.quizzes.passRate}% pass rate`}
                                    icon={Award}
                                    color="blue"
                                />
                                <StatCard
                                    title="Overdue Tasks"
                                    value={overview.tasks.overdue}
                                    subtitle="Require attention"
                                    icon={AlertTriangle}
                                    color={overview.tasks.overdue > 0 ? 'red' : 'gray'}
                                />
                            </div>

                            {/* Phase Progress */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500 mb-1">Preboarding Progress</p>
                                        <div className="flex items-baseline gap-2">
                                            <h3 className="text-2xl font-bold text-gray-900">{overview.tasks.preboarding?.progress || 0}%</h3>
                                            <span className="text-sm text-gray-500">completion rate</span>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2">
                                            {overview.tasks.preboarding?.completed || 0} / {overview.tasks.preboarding?.total || 0} tasks
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                                        <Clock className="w-6 h-6 text-blue-500" />
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500 mb-1">Onboarding Progress</p>
                                        <div className="flex items-baseline gap-2">
                                            <h3 className="text-2xl font-bold text-gray-900">{overview.tasks.onboarding?.progress || 0}%</h3>
                                            <span className="text-sm text-gray-500">completion rate</span>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2">
                                            {overview.tasks.onboarding?.completed || 0} / {overview.tasks.onboarding?.total || 0} tasks
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                                        <TrendingUp className="w-6 h-6 text-blue-500" />
                                    </div>
                                </div>
                            </div>

                            {/* Signature & Proof Overview */}
                            {(overview.signatures?.totalRequired > 0 || overview.proofUploads?.totalRequired > 0) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {overview.signatures?.totalRequired > 0 && (
                                        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <PenTool className="w-4 h-4 text-blue-600" />
                                                    <p className="text-sm font-medium text-gray-500">E-Signatures</p>
                                                </div>
                                                <h3 className="text-2xl font-bold text-gray-900">{overview.signatures.completionRate}%</h3>
                                                <p className="text-xs text-gray-400 mt-1">{overview.signatures.signed} / {overview.signatures.totalRequired} signed</p>
                                            </div>
                                            {overview.signatures.pending > 0 && (
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                                    {overview.signatures.pending} pending
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {overview.proofUploads?.totalRequired > 0 && (
                                        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Upload className="w-4 h-4 text-blue-600" />
                                                    <p className="text-sm font-medium text-gray-500">Proof Uploads</p>
                                                </div>
                                                <h3 className="text-2xl font-bold text-gray-900">{overview.proofUploads.submissionRate}%</h3>
                                                <p className="text-xs text-gray-400 mt-1">{overview.proofUploads.submitted} / {overview.proofUploads.totalRequired} submitted</p>
                                            </div>
                                            {overview.proofUploads.pending > 0 && (
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                                    {overview.proofUploads.pending} pending
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Charts Row */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Employee Status Distribution */}
                                <ExpandableVisualization
                                    title="Employee Status"
                                    data={[
                                        { label: 'Active', value: overview.employees.active },
                                        { label: 'Onboarding', value: overview.employees.onboarding },
                                        { label: 'Preboarding', value: overview.employees.preboarding },
                                        { label: 'Offboarded', value: overview.employees.offboarded }
                                    ]}
                                    columns={[{ key: 'label', label: 'Status' }, { key: 'value', label: 'Count' }]}
                                    exportFilename="employee-status"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Employee Status</h3>
                                        <DonutChart
                                            data={[
                                                { label: 'Active', value: overview.employees.active, color: '#3B82F6' },
                                                { label: 'Onboarding', value: overview.employees.onboarding, color: '#60A5FA' },
                                                { label: 'Preboarding', value: overview.employees.preboarding, color: '#93C5FD' },
                                                { label: 'Offboarded', value: overview.employees.offboarded, color: '#9CA3AF' }
                                            ]}
                                            size={140}
                                            totalLabel="Employees"
                                        />
                                    </div>
                                </ExpandableVisualization>

                                {/* Task Status Distribution */}
                                <ExpandableVisualization
                                    title="Task Status"
                                    data={[
                                        { label: 'Completed', value: overview.tasks.completed },
                                        { label: 'In Progress', value: overview.tasks.inProgress },
                                        { label: 'Pending', value: overview.tasks.pending },
                                        { label: 'Overdue', value: overview.tasks.overdue }
                                    ]}
                                    columns={[{ key: 'label', label: 'Status' }, { key: 'value', label: 'Count' }]}
                                    exportFilename="task-status"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Task Status</h3>
                                        <DonutChart
                                            data={[
                                                { label: 'Completed', value: overview.tasks.completed, color: '#3B82F6' },
                                                { label: 'In Progress', value: overview.tasks.inProgress, color: '#60A5FA' },
                                                { label: 'Pending', value: overview.tasks.pending, color: '#93C5FD' },
                                                { label: 'Overdue', value: overview.tasks.overdue, color: '#EF4444' }
                                            ]}
                                            size={140}
                                            totalLabel="Tasks"
                                        />
                                    </div>
                                </ExpandableVisualization>

                                {/* Completion Progress */}
                                <div className="bg-white rounded-xl border border-gray-200 p-6 lg:col-span-2 flex flex-col">
                                    <h3 className="font-semibold text-gray-800 mb-4">Overall Progress</h3>
                                    <div className="flex items-center justify-around">
                                        <ProgressRing
                                            value={overview.tasks.completionRate}
                                            label="Tasks"
                                            sublabel="Completion Rate"
                                        />
                                        <ProgressRing
                                            value={overview.quizzes.passRate}
                                            label="Quizzes"
                                            sublabel="Pass Rate"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Trends Chart */}
                            {trendsData && trendsData.taskCompletions && (
                                <ExpandableVisualization
                                    title="Task Completions Over Time"
                                    data={trendsData.taskCompletions.map(d => ({ date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), completions: d.completions }))}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'completions', label: 'Completions' }]}
                                    exportFilename="overview-completions-trend"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <TrendingUp className="w-5 h-5 text-blue-600" />
                                            <h3 className="font-semibold text-gray-800">Task Completions Over Time</h3>
                                        </div>
                                        <LineChart
                                            data={trendsData.taskCompletions.map(d => ({
                                                date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                value: d.completions
                                            }))}
                                            height={200}
                                            lineColor="#3B82F6"
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            <div className="grid grid-cols-1 gap-6">

                                {/* Quick Stats */}
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <h3 className="font-semibold text-gray-800 mb-4">Quick Stats</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-600">Avg Completion Time</span>
                                            <span className="font-semibold text-gray-900">{formatTime(overview.tasks.avgCompletionTimeMinutes)}</span>
                                        </div>
                                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-600">Survey Response Rate</span>
                                            <span className="font-semibold text-gray-900">{overview.surveys.responseRate}%</span>
                                        </div>
                                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-600">Course Avg Progress</span>
                                            <span className="font-semibold text-gray-900">{overview.courses.avgProgress}%</span>
                                        </div>
                                        <div className="flex items-center justify-between py-2">
                                            <span className="text-gray-600">Compliance Tasks Completed</span>
                                            <span className="font-semibold text-gray-900">{overview.compliance.completed}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Placeholder Analytics Section */}
                            {placeholderData && (
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                    <button
                                        onClick={() => setShowPlaceholders(!showPlaceholders)}
                                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="p-1.5 bg-blue-50 rounded-lg">
                                                <Puzzle className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div className="text-left">
                                                <h3 className="font-semibold text-gray-800">Placeholder Usage</h3>
                                                <p className="text-xs text-gray-500">
                                                    {placeholderData.summary?.adoptionRate || 0}% adoption rate · {placeholderData.summary?.itemsWithPlaceholders || 0} items using placeholders
                                                </p>
                                            </div>
                                        </div>
                                        <svg className={`w-5 h-5 text-gray-400 transition-transform ${showPlaceholders ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </button>

                                    {showPlaceholders && (
                                        <div className="px-6 pb-6 space-y-5 border-t border-gray-100 pt-4">
                                            {/* Summary Stats */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                <div className="text-center p-3 bg-gray-50 rounded-lg">
                                                    <p className="text-xl font-bold text-gray-900">{placeholderData.summary?.totalCourseItems || 0}</p>
                                                    <p className="text-xs text-gray-500">Total Items</p>
                                                </div>
                                                <div className="text-center p-3 bg-blue-50 rounded-lg">
                                                    <p className="text-xl font-bold text-blue-700">{placeholderData.summary?.itemsWithPlaceholders || 0}</p>
                                                    <p className="text-xs text-gray-500">Using Placeholders</p>
                                                </div>
                                                <div className="text-center p-3 bg-gray-50 rounded-lg">
                                                    <p className="text-xl font-bold text-gray-600">{placeholderData.summary?.itemsWithoutPlaceholders || 0}</p>
                                                    <p className="text-xs text-gray-500">Direct Assignment</p>
                                                </div>
                                                <div className="text-center p-3 bg-blue-50 rounded-lg">
                                                    <p className="text-xl font-bold text-blue-700">{placeholderData.summary?.adoptionRate || 0}%</p>
                                                    <p className="text-xs text-gray-500">Adoption Rate</p>
                                                </div>
                                            </div>

                                            {/* Placeholder Breakdown */}
                                            {placeholderData.placeholderBreakdown && placeholderData.placeholderBreakdown.length > 0 && (
                                                <div>
                                                    <h4 className="font-medium text-gray-700 mb-3 text-sm">Placeholder Breakdown</h4>
                                                    <div className="space-y-2">
                                                        {placeholderData.placeholderBreakdown.map((p, idx) => (
                                                            <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono font-medium">
                                                                    {p.slug}
                                                                </span>
                                                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                                                    <span><span className="font-semibold text-gray-800">{p.courseCount}</span> course{p.courseCount !== 1 ? 's' : ''}</span>
                                                                    <span><span className="font-semibold text-gray-800">{p.itemCount}</span> item{p.itemCount !== 1 ? 's' : ''}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Missing Assignments */}
                                            {placeholderData.missingAssignments?.counts && (
                                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                                    <div className="flex items-start gap-2 mb-3">
                                                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                                        <div>
                                                            <h4 className="font-medium text-amber-800 text-sm">Missing Assignments</h4>
                                                            <p className="text-xs text-amber-600">Employees missing key relationships</p>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                        {[
                                                            { key: 'missing_manager', label: 'No Manager' },
                                                            { key: 'missing_buddy', label: 'No Buddy' },
                                                            { key: 'missing_hr_contact', label: 'No HR Contact' },
                                                            { key: 'missing_team_lead', label: 'No Team Lead' }
                                                        ].map(item => (
                                                            <div key={item.key} className="bg-white/60 rounded-lg p-2 text-center">
                                                                <p className="text-lg font-bold text-amber-800">
                                                                    {placeholderData.missingAssignments.counts[item.key] || 0}
                                                                </p>
                                                                <p className="text-[10px] text-amber-600">{item.label}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Custom Placeholder Coverage */}
                                            {placeholderData.customPlaceholders && placeholderData.customPlaceholders.length > 0 && (
                                                <div>
                                                    <h4 className="font-medium text-gray-700 mb-3 text-sm">Custom Placeholder Coverage</h4>
                                                    <div className="space-y-2">
                                                        {placeholderData.customPlaceholders.map((cp, idx) => (
                                                            <div key={idx}>
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm font-medium text-gray-700">{cp.name}</span>
                                                                    <span className="text-xs text-gray-500">{cp.assignedCount}/{cp.totalEmployees} ({cp.coverageRate}%)</span>
                                                                </div>
                                                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                    <div
                                                                        className={`h-full rounded-full ${cp.coverageRate >= 80 ? 'bg-blue-600' : cp.coverageRate >= 40 ? 'bg-blue-400' : 'bg-red-500'}`}
                                                                        style={{ width: `${cp.coverageRate}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Missing Employees Table */}
                                            {placeholderData.missingAssignments?.employees && placeholderData.missingAssignments.employees.length > 0 && (
                                                <div>
                                                    <h4 className="font-medium text-gray-700 mb-3 text-sm">Employees With Missing Assignments</h4>
                                                    <DataTable
                                                        data={placeholderData.missingAssignments.employees}
                                                        columns={[
                                                            { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                            { key: 'email', label: 'Email' },
                                                            {
                                                                key: 'lifecycleStage', label: 'Stage', render: (val) => (
                                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${val === 'preboarding' ? 'bg-amber-100 text-amber-700' :
                                                                        val === 'onboarding' ? 'bg-blue-100 text-blue-700' :
                                                                            'bg-green-100 text-green-700'
                                                                        }`}>{val}</span>
                                                                )
                                                            },
                                                            {
                                                                key: 'missing', label: 'Missing', render: (val) => (
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {val.map(m => (
                                                                            <span key={m} className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-medium">
                                                                                {m.replace('_', ' ')}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )
                                                            }
                                                        ]}
                                                        searchable
                                                        searchKeys={['name', 'email']}
                                                        pageSize={5}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* AI Insights Panel */}
                            <AnalyticsAIPanel
                                analyticsContext={{
                                    type: 'overview',
                                    title: 'Analytics Overview',
                                    data: overview,
                                }}
                            />

                            {/* Reminder Activity Summary */}
                            {reminderSummary && reminderSummary.totalRemindersThisWeek > 0 && (
                                <div className="bg-white rounded-xl border border-gray-200 p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="p-1.5 bg-blue-50 rounded-lg">
                                            <Send className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm">Reminder Activity</h3>
                                            <p className="text-xs text-gray-500">Last 7 days</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                                            <p className="text-xl font-bold text-blue-700">{reminderSummary.totalRemindersThisWeek}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">Reminders Sent</p>
                                        </div>
                                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center justify-center gap-1">
                                                <Zap className="w-3.5 h-3.5 text-amber-500" />
                                                <p className="text-xl font-bold text-gray-800">{reminderSummary.automatedCount}</p>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">Automated</p>
                                        </div>
                                        <div className="text-center p-3 bg-green-50 rounded-lg">
                                            <p className="text-xl font-bold text-green-700">{reminderSummary.effectivenessRate}%</p>
                                            <p className="text-xs text-gray-500 mt-0.5">Effectiveness</p>
                                        </div>
                                    </div>
                                    {reminderSummary.manualCount > 0 && (
                                        <p className="text-xs text-gray-500 mt-3 text-center">
                                            {reminderSummary.manualCount} manual · {reminderSummary.automatedCount} automated · {reminderSummary.totalRecipients} total recipients
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Reminder History & Effectiveness */}
                            <ReminderHistory />

                            {/* Notification Settings Panel */}
                            <NotificationSettingsPanel
                                analyticsContext={{
                                    type: 'overview',
                                    title: 'Analytics Overview',
                                    data: overview,
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <p>No analytics data available</p>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default OverviewAnalyticsDrawer;
